import useSWR from "swr";
import { api, fmtEur, fmtNum } from "@/lib/app";
import { ShoppingCart } from "@phosphor-icons/react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  LineChart,
  Cell,
} from "recharts";

const fetcher = (url) => api.get(url).then((r) => r.data);

/**
 * Zákazníci / Nákupy — hour-of-day view of purchase volume + basket size,
 * compared side-by-side with hourly performance target and employee headcount.
 *
 * Answers: "koľko nákupov v ktorej hodine, aký je priemerný nákup,
 * a ako to sedí voči hodinovému výkonu a personálu na zmene?"
 */
function CustomChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-white border border-[var(--border-default)] rounded-md px-3 py-2 text-[11.5px] shadow-sm tabular">
      <p className="font-semibold mb-1 text-[var(--text-primary)]">
        {label}:00–{label + 1}:00
      </p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
        <span className="text-[var(--text-secondary)]">Nákupy/hod</span>
        <span className="text-right font-semibold">{fmtNum(row.customers, 1)}</span>
        <span className="text-[var(--text-secondary)]">Priem. košík</span>
        <span className="text-right">{fmtEur(row.basket)}</span>
        <span className="text-[var(--text-secondary)]">Zamestnanci</span>
        <span className="text-right">{fmtNum(row.employees, 2)}</span>
        <span className="text-[var(--text-secondary)]">€ / os. hod</span>
        <span className="text-right">{fmtEur(row.rev_per_emp)}</span>
        <span className="text-[var(--text-secondary)]">Nákupov / os.</span>
        <span className="text-right">{fmtNum(row.cust_per_emp, 1)}</span>
        {row.required_performance > 0 && (
          <>
            <span className="text-[var(--text-secondary)]">Cieľový výkon</span>
            <span className="text-right">{fmtEur(row.required_performance)}</span>
            <span className="text-[var(--text-secondary)]">Achievement</span>
            <span
              className="text-right font-semibold"
              style={{
                color:
                  row.perf_index >= 1
                    ? "#3A7148"
                    : row.perf_index >= 0.8
                    ? "#C77E20"
                    : "#B54A3B",
              }}
            >
              {fmtNum(row.perf_index * 100, 0)}%
            </span>
          </>
        )}
      </div>
    </div>
  );
}

export default function PurchasesByHour({ storeId, period }) {
  const qs = `period_from=${period?.from || ""}&period_to=${period?.to || ""}`;
  const { data, error } = useSWR(`/stores/${storeId}/analytics/customers?${qs}`, fetcher);

  if (error) return null;
  if (!data)
    return (
      <div className="card-flat p-5 text-sm text-[var(--text-secondary)]" data-testid="purchases-by-hour-loading">
        Načítavam hodinový profil…
      </div>
    );
  if (data.empty || !data.by_hour?.length) return null;

  const req = data.by_hour[0]?.required_performance || 0;
  const totalPurchases = data.totals.customers;
  const avgBasket = data.totals.avg_basket;
  const perfAvg =
    data.by_hour.length && req
      ? data.by_hour.reduce((a, r) => a + (r.perf_index || 0), 0) / data.by_hour.length
      : 0;

  return (
    <div className="card-flat p-5 space-y-5" data-testid="purchases-by-hour">
      <div className="flex items-start gap-3">
        <ShoppingCart size={22} weight="duotone" className="text-[var(--brand)] mt-1 shrink-0" />
        <div>
          <h3 className="font-display text-base font-semibold">Zákazníci / Nákupy podľa hodiny</h3>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            Priemerný počet nákupov a hodnota košíka pre každú hodinu — s kontextom
            hodinového výkonu ({fmtEur(req)}/os) a počtu zamestnancov na zmene.
          </p>
        </div>
      </div>

      {/* Chart 1: Purchases per hour (bars) + basket per hour (line, right axis) */}
      <div>
        <h4 className="font-display text-[13px] font-semibold mb-1">
          Objem nákupov &amp; priemerný košík / hodinu
        </h4>
        <p className="text-[11px] text-[var(--text-secondary)] mb-2">
          Bars = počet nákupov (priemer na deň), línia = priemerný nákup v €. Kdekoľvek košík klesá pri raste nákupov, ide o rýchle drobné nákupy.
        </p>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={data.by_hour} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="hour"
              tick={{ fontSize: 11, fill: "#5E6962" }}
              tickFormatter={(h) => `${h}h`}
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 11, fill: "#5E6962" }}
              label={{ value: "Nákupy/hod", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#5E6962" } }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11, fill: "#5E6962" }}
              tickFormatter={(v) => `${v}€`}
              label={{ value: "Košík", angle: 90, position: "insideRight", style: { fontSize: 10, fill: "#5E6962" } }}
            />
            <Tooltip content={<CustomChartTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="left" dataKey="customers" name="Nákupy/hod" fill="#8FB69A" radius={[3, 3, 0, 0]} isAnimationActive={false} />
            <Line yAxisId="right" type="monotone" dataKey="basket" name="Priem. košík (€)" stroke="#C78220" strokeWidth={2.5} dot={{ r: 3 }} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Chart 2: rev_per_emp vs required_performance — hidden if no target set */}
      {req > 0 && (
      <div>
        <h4 className="font-display text-[13px] font-semibold mb-1">
          Tržba na 1 zamestnanca/hod vs. cieľový hodinový výkon
        </h4>
        <p className="text-[11px] text-[var(--text-secondary)] mb-2">
          Zelená čiara = cieľový výkon ({fmtEur(req)}/os.). Bars <span className="font-semibold" style={{ color: "#3A7148" }}>zelené = nad cieľom</span>,{" "}
          <span className="font-semibold" style={{ color: "#C77E20" }}>oranžové = 80-99% cieľa</span>,{" "}
          <span className="font-semibold" style={{ color: "#B54A3B" }}>červené = pod 80%</span>.
          Priemerný achievement v období: <strong>{fmtNum(perfAvg * 100, 0)}%</strong>.
        </p>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={data.by_hour} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="hour" tick={{ fontSize: 11, fill: "#5E6962" }} tickFormatter={(h) => `${h}h`} />
            <YAxis tick={{ fontSize: 11, fill: "#5E6962" }} tickFormatter={(v) => `${v}€`} />
            <Tooltip content={<CustomChartTooltip />} />
            {req > 0 && (
              <ReferenceLine
                y={req}
                stroke="#3A7148"
                strokeWidth={2}
                strokeDasharray="5 3"
                label={{ value: `cieľ ${fmtEur(req)}`, position: "insideTopRight", fontSize: 10, fill: "#3A7148" }}
              />
            )}
            <Bar
              dataKey="rev_per_emp"
              name="Tržba/os.hod (€)"
              radius={[3, 3, 0, 0]}
              isAnimationActive={false}
            >
              {data.by_hour.map((r, i) => {
                const pct = r.perf_index || 0;
                const fill = pct >= 1 ? "#8FB69A" : pct >= 0.8 ? "#E5B063" : "#D8846F";
                return <Cell key={i} fill={fill} />;
              })}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      )}

      {/* Chart 3: cust_per_emp — how many purchases each employee handles per hour */}
      <div>
        <h4 className="font-display text-[13px] font-semibold mb-1">
          Nákupov / 1 zamestnanec / hodinu
        </h4>
        <p className="text-[11px] text-[var(--text-secondary)] mb-2">
          Priemer strediska:{" "}
          <strong>{fmtNum(data.totals.customers_per_emp_hour, 1)}</strong> nákupov/os.hod. Rovnaké bary sú v pásmovke — vidno kde jeden pracovník zvládne extrém, a kde má voľnejšie.
        </p>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data.by_hour} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="hour" tick={{ fontSize: 11, fill: "#5E6962" }} tickFormatter={(h) => `${h}h`} />
            <YAxis tick={{ fontSize: 11, fill: "#5E6962" }} />
            <Tooltip content={<CustomChartTooltip />} />
            <ReferenceLine
              y={data.totals.customers_per_emp_hour}
              stroke="#5E6962"
              strokeDasharray="4 3"
              label={{ value: "Ø", position: "insideTopRight", fontSize: 10, fill: "#5E6962" }}
            />
            <Line type="monotone" dataKey="cust_per_emp" name="Nákupov/os.hod" stroke="#2B4C38" strokeWidth={2.5} dot={{ r: 3 }} isAnimationActive={false} />
            <Line type="monotone" dataKey="employees" name="Zamestnanci/hod" stroke="#C78220" strokeWidth={2} strokeDasharray="4 3" dot={false} isAnimationActive={false} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t border-[var(--border-default)]">
        <div>
          <p className="label-eyebrow mb-0.5">Nákupy spolu</p>
          <p className="font-display text-lg font-bold tabular">{fmtNum(totalPurchases, 0)}</p>
        </div>
        <div>
          <p className="label-eyebrow mb-0.5">Priemerný košík</p>
          <p className="font-display text-lg font-bold tabular">{fmtEur(avgBasket)}</p>
        </div>
        <div>
          <p className="label-eyebrow mb-0.5">Nákupov/os.hod (Ø)</p>
          <p className="font-display text-lg font-bold tabular">{fmtNum(data.totals.customers_per_emp_hour, 1)}</p>
        </div>
        <div>
          <p className="label-eyebrow mb-0.5">Achievement voči cieľu</p>
          <p
            className="font-display text-lg font-bold tabular"
            style={{
              color: perfAvg >= 1 ? "#3A7148" : perfAvg >= 0.8 ? "#C77E20" : "#B54A3B",
            }}
          >
            {req > 0 ? `${fmtNum(perfAvg * 100, 0)}%` : "—"}
          </p>
        </div>
      </div>
    </div>
  );
}
